import _ from 'lodash'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'

import Stack from '@mui/material/Stack'

import RadioGroupInput from '@/components/ui/inputs/RadioGroupInput'

import { useUpdateMyProfileMutation } from '@/core/services/auth'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { isStaff } from '@/utils/type-guards'
import { getApiErrorMessage } from '@/utils/errors'

export type DisplayChatFormValues = {
  displayChat: 'true' | 'false'
}

export function DisplayChatForm() {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const [updateMyProfile] = useUpdateMyProfileMutation()

  const displayChat = useMemo(() => (isStaff(authUser) && authUser.displayChat ? 'true' : 'false'), [authUser])

  const rhForm = useForm<DisplayChatFormValues>({ defaultValues: { displayChat } })
  const { handleSubmit, reset, subscribe } = rhForm

  const options = useMemo(
    () => [
      { label: t('page.configuration.chat.true'), value: true },
      { label: t('page.configuration.chat.false'), value: false },
    ],
    [t]
  )

  useEffect(() => {
    return subscribe({
      name: 'displayChat',
      formState: { values: true },
      callback: () => {
        handleSubmit(async (data) => {
          try {
            await updateMyProfile({ displayChat: data.displayChat === 'true' }).unwrap()
            enqueueSnackbar(t('notification.authUser.displayChat.success'), { variant: 'success' })
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.authUser.displayChat.error')), { variant: 'error' })
            reset()
          }
        })()
      },
    })
  }, [handleSubmit, reset, subscribe])

  return (
    <form>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={1}>
        <Controller
          control={rhForm.control}
          name="displayChat"
          render={({ field }) => <RadioGroupInput fullWidth row options={options} variant="standard" onChange={field.onChange} value={field.value} />}
        />
      </Stack>
    </form>
  )
}

export default DisplayChatForm
